import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { DataLayer } from '../../../models/data-layer';
import { IndicatorData } from '../../../models/indicator-data';
import { DataLayerType } from '../../../models/data-layer-type';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { GeoData } from '../../../models/geo-data';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { ComparisonValueStore } from '../../../stores/comparison-value/comparison-value.state';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-fast-switch-feature',
  standalone: true,
  imports: [TieredMenuModule, NgClass],
  templateUrl: './fast-switch-feature.component.html',
  styleUrl: './fast-switch-feature.component.scss',
})
export class FastSwitchFeatureComponent implements OnInit, OnChanges {
  readonly geoStore = inject(GeoDataStore);
  readonly layerStore = inject(DataLayerStore);
  readonly typeStore = inject(DataLayerTypeStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly comparisonValueStore = inject(ComparisonValueStore);

  @Input() readonly = false;
  @Input() featureKey!: string;
  @Input() indicatorIds: string[] = [];
  @Input() dataLayerId = '';
  @Output() changeFeature = new EventEmitter<{
    feature: any;
    layer: DataLayer;
  }>();

  availableDataByIndicators?: Record<string, Record<string, IndicatorData>>;
  feature: any | null = null;
  dataLayer?: DataLayer;
  geoData?: GeoData;
  dataLayerType?: DataLayerType;
  featureMenu: MenuItem[] = [];
  typeLayers: Record<string, DataLayer[]> = {};

  ngOnInit() {
    this.init();
  }

  /**
   * Method called when Angular detects changes to the component's input properties.
   * Executes custom logic whenever specific input changes are detected.
   *
   * @param {SimpleChanges} changes - An object that contains the current and previous values of changed input properties.
   * @return {void} This method does not return a value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['indicatorIds']) {
      this.init();
    }
  }

  /**
   * Initializes the necessary data layers, type layers, and features for the application functionality.
   * It retrieves and organizes required data based on the provided data layer ID, indicator IDs, and other properties.
   *
   * @return {void} This method does not return a value.
   */
  init(): void {
    this.dataLayer = this.layerStore.getById(this.dataLayerId);
    this.typeLayers = this.layerStore.groupedByType();
    if (!this.indicatorIds.length) return;

    const availableData = this.dataStore.getGroupedByIndicators(
      this.indicatorIds
    );

    this.buildFeatureMenu(availableData);

    if (this.dataLayer && this.dataLayer.geoData) {
      this.dataLayerType = this.typeStore.getById(this.dataLayer.parent);

      this.feature = this.geoStore.getDistrictByIdAndKey(
        this.dataLayer.geoData,
        this.featureKey
      );

      if (!this.feature) {
        this.feature = this.comparisonValueStore.byKey()[this.featureKey];
      }
    }
  }

  /**
   * Builds the feature menu based on the available data, generating menu items for different types and their associated features.
   *
   * @param {Record<string, Record<string, IndicatorData>>} availableData - An object mapping type IDs to their respective indicator data.
   * @return {void} This method does not return any value but updates the `featureMenu` property.
   */
  private buildFeatureMenu(
    availableData: Record<string, Record<string, IndicatorData>>
  ): void {
    this.featureMenu = [];
    for (const typeId in availableData) {
      const typeLayers = availableData[typeId];
      const typeLayerIds = Object.keys(typeLayers);
      const type = this.typeStore.getById(typeId);
      const latestLayer = this.layerStore.getById(
        typeLayerIds[typeLayerIds.length - 1]
      );
      if (!latestLayer || !latestLayer.geoData) continue;
      const latestFeatures = this.geoStore.getDistrictsById(
        latestLayer.geoData
      );
      const comparisonValues = this.comparisonValueStore.byKey();
      const featureItems: MenuItem[] = [];
      const withComparisons = { ...latestFeatures, ...comparisonValues };

      for (const featureKey in withComparisons) {
        const feature = withComparisons[featureKey];
        featureItems.push({
          icon: 'fas fa-map-location-dot',
          label: feature.name,
          id: featureKey,
          command: (event: MenuItemCommandEvent) => {
            this.changeFeature.emit({ feature, layer: latestLayer });
          },
        });
      }

      this.featureMenu.push({
        icon:
          type.type === 'Point' ? 'fas fa-location-dot' : 'fa-regular fa-map',
        label: type.name,
        items: featureItems,
      });
    }
  }
}
